import React, { useState, useMemo, useEffect, useRef } from "react";
import { View, StyleSheet, Linking } from "react-native";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { PermissionsType } from "@/types/db/roles";
import useContextMenu from "@/hooks/useContextMenu";
import { useCall } from "@/hooks/useCall";
import { callService, CallHistoryRecord } from "@/services/callService";

export interface CallButtonProps {
  /** The destination phone number to call */
  phoneNumber: string;
  /** Button size */
  size?: number;
}

export function CallButton({ phoneNumber, size = 36 }: CallButtonProps) {
  const { userRoles } = useAuth();
  const { colors } = useTheme();
  const call = useCall();
  const contextMenu = useContextMenu();
  const ref = useRef<View>(null);
  const [hasFaceTime, setHasFaceTime] = useState(false);
  const [recentCall, setRecentCall] = useState<CallHistoryRecord | null>(null);

  const phones = useMemo(() => {
    if (!userRoles) return [];
    const allPhones = userRoles.flatMap((r) => r.permissions.phones || []);
    const uniqueMap = new Map();
    allPhones.forEach((phone) => {
      if (!uniqueMap.has(phone.number)) {
        uniqueMap.set(phone.number, phone);
      }
    });
    return Array.from(uniqueMap.values());
  }, [userRoles]);

  useEffect(() => {
    Linking.canOpenURL("facetime-audio://")
      .then(setHasFaceTime)
      .catch(() => setHasFaceTime(false));
  }, []);

  useEffect(() => {
    const loadRecentCall = async () => {
      try {
        const calls = await callService.getCallsForNumber(phoneNumber, 1);
        if (calls.length > 0) {
          setRecentCall(calls[0]);
        }
      } catch (error) {
        // Silently fail
      }
    };
    loadRecentCall();
  }, [phoneNumber]);

  const handleTwilioCall = (fromPhone: PermissionsType["phones"][number]) => {
    call.startCall({
      callerName: fromPhone.alias || fromPhone.number,
      phoneNumber: phoneNumber,
      fromNumber: fromPhone.number,
    });
    contextMenu.setVisible(false);
  };

  const handleFaceTimeAudio = async () => {
    const url = `facetime-audio:${phoneNumber}`;
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
      contextMenu.setVisible(false);
    }
  };

  const handleDeviceDialer = async () => {
    const url = `tel:${phoneNumber}`;
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
      contextMenu.setVisible(false);
    }
  };

  const menuContent = (
    <View style={[styles.menu, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {phones.map((phone) => {
        const displayText = phone.show_number ? phone.number : phone.alias || phone.number;
        return (
          <Button
            key={phone.number}
            text={displayText}
            iconSize={18}
            icon="phone"
            style={styles.menuItem}
            onPress={() => handleTwilioCall(phone)}
          />
        );
      })}
      {phones.length > 0 && <View style={{ height: 1, backgroundColor: colors.border, marginVertical: 4 }} />}
      {hasFaceTime ? (
        <Button
          text="FaceTime Audio"
          iconSize={18}
          icon="video"
          style={styles.menuItem}
          onPress={handleFaceTimeAudio}
        />
      ) : (
        <Button
          text="Device Dialer"
          iconSize={18}
          icon="phone-classic"
          style={styles.menuItem}
          onPress={handleDeviceDialer}
        />
      )}
    </View>
  );

  return (
    <Button
      ref={ref}
      icon="phone"
      style={[styles.callButton, { width: size, height: size }]}
      onPress={() => {
        contextMenu.setIgnoreRef(ref);
        ref.current?.measureInWindow((pageX, pageY) => contextMenu.setLocation({ x: pageX - 120, y: pageY + 40 }));
        contextMenu.setMenuContent(menuContent);
        contextMenu.setVisible((p) => !p);
        contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
      }}
    />
  );
}

const styles = StyleSheet.create({
  callButton: {
    paddingHorizontal: 0,
  },
  menu: {
    borderWidth: 0,
  },
  menuItem: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
});
